import { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import {
  Building2,
  Users,
  UserPlus,
  Brain,
  HeartPulse,
  CalendarDays,
  Settings,
  KeyRound,
  LogOut,
  Sparkles,
  MessagesSquare,
  BookOpen,
  Menu,
  X,
  LayoutDashboard,
} from "lucide-react";

const EVENTS_LINK = { to: "/me/upcoming-events", labelKey: "nav.upcoming_events", icon: CalendarDays };
const GOT_LINK = { to: "/me/got-practice", labelKey: "nav.got_practice", icon: MessagesSquare };
const MANUAL_LINK = { to: "/me/manual", labelKey: "nav.manual", icon: BookOpen };
const ACCOUNT_LINK = { to: "/me/account", labelKey: "nav.my_account", icon: KeyRound };

const navByRole = {
  super_admin: [
    { to: "/admin/institutes", labelKey: "nav.institutes", icon: Building2 },
    { to: "/me/brain-type", labelKey: "nav.brain_type", icon: Brain },
    EVENTS_LINK,
    ACCOUNT_LINK,
  ],
  institute_admin: [
    { to: "/institute/coaches", labelKey: "nav.coaches", icon: Users },
    { to: "/me/brain-type", labelKey: "nav.brain_type", icon: Brain },
    EVENTS_LINK,
    { to: "/institute/settings", labelKey: "nav.institute_settings", icon: Settings },
    ACCOUNT_LINK,
  ],
  coach: [
    { to: "/coach/users", labelKey: "nav.clients", icon: UserPlus },
    { to: "/me/brain-type", labelKey: "nav.brain_type", icon: Brain },
    MANUAL_LINK,
    GOT_LINK,
    EVENTS_LINK,
    ACCOUNT_LINK,
  ],
  user: [
    { to: "/me/dashboard", labelKey: "nav.dashboard", icon: LayoutDashboard },
    { to: "/me/quality-of-life", labelKey: "nav.quality_of_life", icon: HeartPulse },
    { to: "/me/brain-type", labelKey: "nav.brain_type", icon: Brain },
    MANUAL_LINK,
    GOT_LINK,
    EVENTS_LINK,
    ACCOUNT_LINK,
  ],
};

const roleLabel = {
  super_admin: "role.super_admin",
  institute_admin: "role.institute_admin",
  coach: "role.coach",
  user: "role.user",
};

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const { t } = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const items = navByRole[user?.role] || [];
  const [navOpen, setNavOpen] = useState(false);

  // Auto-close the drawer when the route changes (mobile UX).
  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6] lg:flex">
      {/* Mobile top bar */}
      <header
        className="lg:hidden sticky top-0 z-30 bg-[#F9F8F6]/95 backdrop-blur border-b border-[#E5E2DC] px-4 py-3 flex items-center justify-between"
        data-testid="mobile-topbar"
      >
        <div className="flex items-center gap-2 text-[#1A1D1A] min-w-0">
          <Sparkles strokeWidth={1.5} className="text-[#B37A5F] shrink-0" size={20} />
          <span className="font-heading text-base tracking-tight font-semibold truncate">
            NeuroSophia
          </span>
        </div>
        <button
          type="button"
          onClick={() => setNavOpen(true)}
          aria-label={t("nav.open")}
          data-testid="mobile-nav-open"
          className="h-10 w-10 inline-flex items-center justify-center rounded-lg border border-[#E5E2DC] bg-white hover:bg-[#F2F0EA] transition-colors"
        >
          <Menu strokeWidth={1.5} size={20} />
        </button>
      </header>

      {/* Mobile backdrop */}
      {navOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/40 z-40"
          onClick={() => setNavOpen(false)}
          data-testid="mobile-nav-backdrop"
          aria-hidden="true"
        />
      )}

      {/* Sidebar — drawer on mobile, sticky column on lg+ */}
      <aside
        className={`fixed top-0 left-0 z-50 h-screen w-72 max-w-[85vw] lg:max-w-none lg:w-64 shrink-0 border-r border-[#E5E2DC] bg-[#F9F8F6] flex flex-col transform transition-transform duration-200 ease-out lg:sticky lg:translate-x-0 ${
          navOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
        data-testid="app-sidebar"
      >
        <div className="px-6 py-6 lg:py-8 border-b border-[#E5E2DC] relative">
          <div className="flex items-center gap-2 text-[#1A1D1A]">
            <Sparkles strokeWidth={1.5} className="text-[#B37A5F]" size={20} />
            <span className="font-heading text-lg tracking-tight font-semibold">
              NeuroSophia
            </span>
          </div>
          <p
            className="mt-1 text-[11px] italic text-[#5C635A] leading-snug"
            data-testid="brand-tagline"
          >
            {t("brand.tagline")}
          </p>
          <p className="mt-3 text-[10px] tracking-[0.2em] uppercase text-[#8A9188]">
            {t(roleLabel[user?.role] || "role.user")}
          </p>
          {/* Close (mobile only) */}
          <button
            type="button"
            onClick={() => setNavOpen(false)}
            aria-label={t("nav.close")}
            data-testid="mobile-nav-close"
            className="lg:hidden absolute top-4 right-4 h-9 w-9 inline-flex items-center justify-center rounded-lg border border-[#E5E2DC] bg-white hover:bg-[#F2F0EA] transition-colors"
          >
            <X strokeWidth={1.5} size={18} />
          </button>
        </div>

        <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto">
          {items.map(({ to, labelKey, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`nav-${t(labelKey).toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  isActive
                    ? "bg-[#4A5D4E] text-white"
                    : "text-[#1A1D1A] hover:bg-[#F2F0EA]"
                }`
              }
            >
              <Icon strokeWidth={1.5} size={18} />
              <span className="font-medium">{t(labelKey)}</span>
            </NavLink>
          ))}
        </nav>

        <div className="px-3 py-4 border-t border-[#E5E2DC] space-y-3">
          <div className="px-3 flex items-center justify-between gap-2">
            <span className="text-[10px] uppercase tracking-[0.2em] text-[#8A9188] font-bold">
              {t("lang.switcher.label")}
            </span>
            <LanguageSwitcher variant="light" />
          </div>
          <div className="px-3 py-2">
            <p
              className="text-xs text-[#5C635A] font-medium truncate"
              data-testid="sidebar-user-name"
            >
              {user?.name}
            </p>
            <p className="text-[11px] text-[#8A9188] truncate">{user?.email}</p>
          </div>
          <button
            onClick={handleLogout}
            data-testid="sidebar-logout-button"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-[#1A1D1A] hover:bg-[#F2F0EA] transition-colors"
          >
            <LogOut strokeWidth={1.5} size={18} />
            <span className="font-medium">{t("nav.sign_out")}</span>
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0">{children}</main>
    </div>
  );
}
